import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Page controls for a server-paginated list.
 *
 * Shows the range and the total, because "page 3 of 7" answers a different
 * question from "41–60 of 134" and lists are usually opened with the second one
 * in mind — how much is there, and am I near the end of it.
 */
export default function Pagination({
  page,
  pages,
  total,
  limit,
  onChange,
}: {
  page: number;
  pages: number;
  total: number;
  limit: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation();

  // One page and nothing to page through: the controls would be noise.
  if (total === 0 || pages <= 1) return null;

  const from = (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);

  return (
    <div className="flex items-center justify-between gap-4 mt-4">
      <p className="text-sm text-gray-500">
        {t('common.showingRange', { from, to, total })}
      </p>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="btn btn-secondary px-2 disabled:opacity-40"
          aria-label={t('common.previous')}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <span className="text-sm text-gray-600 whitespace-nowrap">
          {t('common.pageOf', { page, pages })}
        </span>

        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= pages}
          className="btn btn-secondary px-2 disabled:opacity-40"
          aria-label={t('common.next')}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
